/**
 * Persistent deploy drawer (§4.9).
 *
 * Anchored to the top of the viewport above the main content. Three
 * visibility states driven by `deployStore`: idle (nothing rendered),
 * active (small pill/minibar), expanded (full panel with stdout +
 * step list + close button).
 *
 * The panel shell mounts `useDeployPoller` from useDeploy.ts to keep
 * job state fresh; this component is purely presentation.
 */
import { useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Copy, X, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDeployStore } from '@/stores/deployStore';
import { cn } from '@/lib/utils';

export function DeployDrawer() {
  const {
    activeJobId,
    visibility,
    status,
    label,
    kind,
    agent,
    steps,
    stdout,
    currentStep,
    progress,
    startedAt,
    finishedAt,
    error,
    exitCode,
    expand,
    collapse,
    dismiss,
  } = useDeployStore();

  const stdoutRef = useRef<HTMLPreElement | null>(null);

  // Auto-scroll stdout to the bottom as it grows.
  useEffect(() => {
    if (visibility === 'expanded' && stdoutRef.current) {
      stdoutRef.current.scrollTop = stdoutRef.current.scrollHeight;
    }
  }, [stdout, visibility]);

  if (visibility === 'idle' || !activeJobId) return null;

  const statusIcon =
    status === 'succeeded' ? (
      <CheckCircle2 className="h-4 w-4 text-success" />
    ) : status === 'failed' || status === 'canceled' ? (
      <XCircle className="h-4 w-4 text-destructive" />
    ) : (
      <Loader2 className="h-4 w-4 animate-spin" />
    );

  const statusLabel =
    status === 'running' || status === 'pending'
      ? currentStep
        ? `${currentStep}…`
        : 'Deploying…'
      : status === 'succeeded'
      ? `Deploy complete${finishedAt && startedAt ? ` in ${duration(startedAt, finishedAt)}` : ''}`
      : status === 'failed'
      ? 'Deploy failed'
      : status === 'canceled'
      ? 'Deploy canceled'
      : 'Deploy';

  const closable = status === 'succeeded' || status === 'failed' || status === 'canceled';

  const pctText = Math.round(progress * 100);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(stdout || '');
    } catch {
      /* clipboard blocked */
    }
  }

  return (
    <div
      className="fixed inset-x-0 top-0 z-50 pointer-events-none"
      role="region"
      aria-label="Deploy status"
    >
      <div
        className={cn(
          'pointer-events-auto mx-auto max-w-5xl shadow-lg border bg-card',
          'rounded-b-lg overflow-hidden',
          visibility === 'expanded' ? 'mt-0' : 'mt-2'
        )}
      >
        <button
          type="button"
          onClick={() => (visibility === 'expanded' ? collapse() : expand())}
          className={cn(
            'w-full flex items-center gap-3 px-4 py-2 text-left',
            'hover:bg-muted/40 transition-colors'
          )}
          aria-expanded={visibility === 'expanded'}
        >
          {statusIcon}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">
              {label ?? `${kind}${agent ? ` · ${agent}` : ''}`}
            </div>
            <div className="text-xs text-muted-foreground">
              {statusLabel}
              {status === 'running' && ` · ${pctText}%`}
            </div>
          </div>
          {visibility === 'expanded' ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {visibility === 'expanded' && (
          <div className="border-t">
            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-0">
              <ol className="border-r py-2 max-h-80 overflow-auto" aria-label="Deploy steps">
                {steps.length === 0 && (
                  <li className="px-4 py-2 text-xs text-muted-foreground italic">
                    Waiting for deploy steps…
                  </li>
                )}
                {steps.map((step, idx) => (
                  <li
                    key={`${step.name}-${idx}`}
                    className={cn(
                      'px-4 py-1.5 text-xs flex items-center gap-2',
                      step.status === 'succeeded' && 'text-muted-foreground',
                      step.status === 'running' && 'text-foreground font-medium',
                      step.status === 'failed' && 'text-destructive font-medium'
                    )}
                  >
                    {step.status === 'succeeded' && <CheckCircle2 className="h-3 w-3 text-success" />}
                    {step.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                    {step.status === 'failed' && <XCircle className="h-3 w-3 text-destructive" />}
                    {step.status === 'pending' && <span className="h-3 w-3 inline-block rounded-full border" />}
                    <span className="truncate">{step.name}</span>
                  </li>
                ))}
              </ol>
              <div className="flex flex-col min-h-0">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
                  <span className="text-xs text-muted-foreground">
                    stdout
                    {exitCode !== undefined && ` · exit ${exitCode}`}
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={handleCopy}>
                      <Copy className="mr-1 h-3 w-3" />
                      Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={dismiss}
                      disabled={!closable}
                      aria-label="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {error && (
                  <div className="px-3 py-2 text-xs text-destructive bg-destructive/5 border-b">
                    {error}
                  </div>
                )}
                <pre
                  ref={stdoutRef}
                  className="flex-1 min-h-[16rem] max-h-80 overflow-auto p-3 bg-black text-zinc-100 text-xs font-mono whitespace-pre-wrap"
                >
                  {stdout || 'Waiting for output…'}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function duration(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms)) return '?';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
